import { isSmartlistItem } from "./typePredicate";
import { SmartlistType } from "../api/MediaAPI.types";
import { PROJECTS_ROUTE } from "../components/routes/routes";

import type {
  SmartlistItem,
  SmartlistItemUpdated,
  UpdatedSmartlist,
  UpdatedSmartlistPayload,
} from "../api/SmartlistAPI.types";
import type { SelectBoxDisplayItem } from "../components/selectBox/selectBox.types";

export const EMPTY_SMARTLIST_ITEM_LIST: SelectBoxDisplayItem[] = [];

/**
 * Maps SelectBox contents (freshly picked items, or already-hydrated
 * SmartlistItem entries) into the `{ content_type, content_uuid, rank }`
 * shape expected by `UpdatedSmartlist.smartlist_items`.
 */
export function mapContentsToSmartlistItems(
  contents: (SelectBoxDisplayItem | SmartlistItem)[],
): SmartlistItemUpdated[] {
  return contents.map((content, index) => {
    if (isSmartlistItem(content)) {
      return {
        content_type: content.content_type,
        content_uuid: content.content_uuid,
        rank: index,
      };
    }
    return {
      // itemType is only unset for not-yet-resolved items, which never reach
      // mapContentsToSmartlistItems (only draggable/resolved items do).
      content_type: content.itemType as string,
      content_uuid: content.uuid,
      rank: index,
    };
  });
}

/**
 * Serializes a smartlist form state into the wire payload expected by
 * `SmartlistAPI.updateSmartlist`. The castings (kept as display objects in the
 * form) are reduced to `{ uuid, rank }` — uuid being the casting's uuid, rank
 * their order.
 */
export function smartlistToApi(
  smartlist: UpdatedSmartlist,
): UpdatedSmartlistPayload {
  const { castings, ...rest } = smartlist;
  return {
    ...rest,
    castings: castings?.map((casting, index) => ({
      uuid: casting.casting_uuid,
      rank: index,
    })),
  };
}

// Editor route slug per smartlist type (mirrors SmartlistPage / SeriePage).
// SEASON has no standalone edit route, so it is intentionally omitted.
const SMARTLIST_TYPE_ROUTE_SLUG: Partial<Record<SmartlistType, string>> = {
  [SmartlistType.STANDARD]: "smartlist",
  [SmartlistType.AUTO]: "smartlist",
  [SmartlistType.AUDIO]: "smartlist_audio",
  [SmartlistType.COLLECTION]: "collections",
  [SmartlistType.SERIES]: "series",
};

/**
 * Builds the editor route for a smartlist, or `undefined` when the type has no
 * standalone edit route (e.g. SEASON) or the project slug is unknown.
 */
export function smartlistEditPath(
  projectSlug: string | undefined,
  type: SmartlistType,
  uuid: string,
): string | undefined {
  const slug = SMARTLIST_TYPE_ROUTE_SLUG[type];
  if (!slug || !projectSlug) return undefined;
  return `${PROJECTS_ROUTE}/${projectSlug}/${slug}/edit/${uuid}`;
}
